import axios from 'axios';
import { getEnvironment as getEnv, getSelectedTenant } from '../config/environment';
import { isTokenExpiringSoon } from '../utils/jwt';
import { toastRef } from '../components/GlobalToast';
import { accessTokenStore } from '../auth/accessTokenStore';
import { ensureFreshAccessToken } from '../auth/ensureFreshAccessToken';

/** In DEV use same time as backend (mock) for token expiry check. Lazy to avoid circular api <-> TimeProvider. */
async function getNowMsForTokenCheck(): Promise<number | undefined> {
  if (!import.meta.env.DEV) return undefined;
  try {
    const { timeProvider } = await import('../services/TimeProvider');
    return timeProvider.getCurrentTimeSync().getTime();
  } catch {
    return undefined;
  }
}

// Cache environment to avoid repeated calls
let environmentCache: { api: { baseUrl: string } } | null = null;

function getEnvironment(): Promise<{ api: { baseUrl: string } }> {
    if (environmentCache) {
        return Promise.resolve(environmentCache);
    }
    // Use getEnvironment() function directly - no dynamic import needed
    // This avoids TDZ issues since we're using a static import
    try {
        const env = getEnv();
        environmentCache = env;
        return Promise.resolve(env);
    } catch (e) {
        // Fallback if getEnvironment() fails
        const fallback = {
            api: {
                baseUrl: import.meta.env.VITE_API_URL || window.location.origin
            }
        };
        environmentCache = fallback;
        return Promise.resolve(fallback);
    }
}

// Get base URL lazily - use a default for immediate initialization
const getBaseUrl = (): string => {
    // Return a default that will be replaced once environment is loaded
    if (environmentCache) {
        return environmentCache.api.baseUrl;
    }
    // Start loading environment in background
    getEnvironment().catch(err => console.error('Failed to load environment:', err));
    // Return a fallback - will be updated once environment loads
    return import.meta.env.VITE_API_URL || window.location.origin;
};

// Create API instance with lazy base URL
export const api = axios.create({
    baseURL: getBaseUrl(),
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Load environment immediately - use static import to avoid TDZ issues
try {
    const env = getEnv();
    api.defaults.baseURL = env.api.baseUrl;
} catch (e) {
    console.warn('Failed to get environment, using fallback URL:', e);
    api.defaults.baseURL = import.meta.env.VITE_API_URL || window.location.origin;
}

// Add request interceptor for authentication and tenant
// Axios typings expect sync/IPromise; native Promise works at runtime.
// Type assertion: some axios typings (AxiosXHRConfig/IPromise) don't accept config | Promise<config>.
api.interceptors.request.use(
    ((config: { url?: string; headers?: Record<string, unknown> }) => {
        // Skip token refresh check for auth endpoints
        const isAuthEndpoint = config.url?.includes('/auth/login') ||
                              config.url?.includes('/api/profile') ||
                              config.url?.includes('/auth/refresh') ||
                              config.url?.includes('/auth/logout');

        // Add auth token if available
        // CRITICAL: Prefer sessionStorage over localStorage for security
        const token = accessTokenStore.get();
        const applyTokenAndHeaders = (tokenToSend: string | null) => {
            if (tokenToSend && config.headers) {
                config.headers.Authorization = `Bearer ${tokenToSend}`;
            }
            if (config.headers) {
                try {
                    const tenant = getSelectedTenant();

                    if (tenant) {
                        const tenantId = tenant.uuid;

                        if (tenantId) {
                            config.headers['X-Tenant-ID'] = tenantId;
                        }
                    }
                } catch (e) {
                    const tenantIdFromSession = sessionStorage.getItem('tenantId');
                    if (tenantIdFromSession && config.headers) {
                        config.headers['X-Tenant-ID'] = tenantIdFromSession;
                    } else {
                        const storedTenant = localStorage.getItem('selectedTenant');
                        if (storedTenant && config.headers) {
                            try {
                                const tenant = JSON.parse(storedTenant);
                                const tenantId = tenant.uuid || tenant.id;
                                if (tenantId) {
                                    config.headers['X-Tenant-ID'] = tenantId;
                                }
                            } catch (_parseError) {
                                // Failed to parse tenant from localStorage
                            }
                        }
                    }
                }
            }
        };

        if (token && !isAuthEndpoint) {
            // An expired or expiring token is refreshed before the request goes out. Dropping the
            // header instead produced a 403 that was treated as an auth error and logged the user
            // out (review round 5, finding 2.3).
            return getNowMsForTokenCheck().then(async (nowMs) => {
                applyTokenAndHeaders(await ensureFreshAccessToken(nowMs));
                return config;
            });
        }
        applyTokenAndHeaders(token);
        return config;
    }) as any,
    (error) => {
        return Promise.reject(error);
    }
);

// Track if we're currently refreshing to avoid infinite loops
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

// Only one "session expired" handling at a time – prevents dozens of toasts and logout requests
let isHandlingAuthError = false;
let sessionExpiredToastShown = false;

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Add response interceptor for error handling and automatic token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        // Skip refresh logic for auth endpoints (login, refresh, logout)
        const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                              originalRequest?.url?.includes('/api/profile') ||
                              originalRequest?.url?.includes('/auth/refresh') ||
                              originalRequest?.url?.includes('/auth/logout');
        
        // Handle 401 errors with automatic token refresh
        if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
            // CRITICAL: Check if token is expired before attempting refresh
            const token = accessTokenStore.get();
            const nowMs = await getNowMsForTokenCheck();
            if (token && isTokenExpiringSoon(token, 0, nowMs)) {
                console.warn('⚠️ Token is expired, attempting refresh...');
            }
            
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Import useStore dynamically to avoid circular dependencies
                const { useStore } = await import('../../store');
                const newToken = await useStore.getState().refreshAccessToken();
                
                // Ensure newToken is a string (refreshAccessToken should return Promise<string>)
                if (typeof newToken !== 'string') {
                    throw new Error('Token refresh failed: invalid token returned');
                }
                
                // Update the token in the original request
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                
                // Process queued requests
                processQueue(null, newToken);
                isRefreshing = false;
                
                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh failed (backend restarted, refresh token invalid, etc.)
                console.error('❌ Token refresh failed:', refreshError);
                processQueue(refreshError, null);
                isRefreshing = false;
                
                // CRITICAL: Clear all auth data and redirect to login immediately
                handleAuthenticationError();
                return Promise.reject(refreshError);
            }
        }
        
        // Handle 403 errors (permission denied)
        if (error.response?.status === 403 && !isAuthEndpoint) {
            console.warn('⚠️ 403 Forbidden - permission denied');
            // Check if it's a token-related 403 (invalid/expired token can sometimes return 403)
            const token = accessTokenStore.get();
            const nowMs403 = await getNowMsForTokenCheck();
            if (token && isTokenExpiringSoon(token, 0, nowMs403)) {
                // Token is expired - treat as authentication error
                console.warn('⚠️ 403 with expired token - treating as auth error');
                handleAuthenticationError();
                return Promise.reject(error);
            }
            // Otherwise, it's a real permission issue - don't redirect, let UI handle it
        }
        
        // Do NOT treat failed login (wrong credentials) as 'session expired'.
        // 401/400 on /auth/login means the user entered a wrong username/password -
        // the LoginPage will show an invalid-credentials message. Only use
        // handleAuthenticationError for an expired session (for example a failed refresh above).
        // So we intentionally do not call handleAuthenticationError for auth endpoints here.

        return Promise.reject(error);
    }
);

// Function to handle authentication errors (single toast, single logout, single redirect)
const handleAuthenticationError = async () => {
    if (isHandlingAuthError) {
        return;
    }
    isHandlingAuthError = true;

    try {
        // Show exactly one toast (prevents dozens of "Sitzung abgelaufen" toasts)
        if (!sessionExpiredToastShown) {
            sessionExpiredToastShown = true;
            toastRef.current?.show({
                title: 'Sitzung abgelaufen',
                content: 'Ihre Sitzung ist abgelaufen. Sie werden zur Anmeldeseite weitergeleitet.',
                cssClass: 'e-toast-error',
            });
            setTimeout(() => { sessionExpiredToastShown = false; }, 5000);
        }

        // Deduplicated logout (store only sends one POST /api/logout)
        const { useStore } = await import('../../store');
        await useStore.getState().logout();

        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    } finally {
        isHandlingAuthError = false;
    }
}; 